<template>
  <div class="contenedor">
    <!-- Box data -->
    <div class="row mb-4">
        <!-- Panel 1: Total Active Accounts -->
        <div v-if="!fromClient" class="col-md-2 col-lg mb-3" style="flex: 0 0 16.66%; max-width: 16.66%;">
          <div class="card shadow-sm h-100" :class="{ 'position-relative': loadingStats, 'opacity-50 pe-none': !statsDataLoaded }">
            <div class="card-body">
              <h6 class="card-title font-weight-bold">Total Active Accounts</h6>
              <div v-if="loadingStats" class="text-center py-3">
                <span class="spinner-border spinner-border-sm text-primary"></span>
              </div>
              <div v-else>
                <h2 class="mb-3" :class="{ 'text-muted': !statsDataLoaded }">
                  {{ statsData.totalActiveAccounts || 0 }}
                </h2>
                <div class="mt-3">
                  <div class="mb-2">
                    <strong>{{ $t("lang.token.order_status") }}</strong>
                  </div>
                  <div class="d-flex flex-column align-items-start gap-2">
                    <span 
                      class="badge badge-success px-2 py-1 my-2"
                      :class="{ 'bg-secondary': !statsDataLoaded }"
                    >
                      {{ (statsData.orderStatus && statsData.orderStatus.active) || 0 }} ({{ (statsData.orderStatus && statsData.orderStatus.activePercent) || 0 }}%)
                    </span>
                    <span 
                      class="badge badge-warning px-2 py-1 my-2"
                      :class="{ 'bg-secondary': !statsDataLoaded }"
                    >
                      {{ (statsData.orderStatus && statsData.orderStatus.preparing) || 0 }} ({{ (statsData.orderStatus && statsData.orderStatus.preparingPercent) || 0 }}%)
                    </span>
                    <span 
                      class="badge badge-danger px-2 py-1 my-2"
                      :class="{ 'bg-secondary': !statsDataLoaded }"
                    >
                      {{ (statsData.orderStatus && statsData.orderStatus.problematic) || 0 }} ({{ (statsData.orderStatus && statsData.orderStatus.problematicPercent) || 0 }}%)
                    </span>
                    <span 
                      class="badge badge-secondary px-2 py-1 my-2"
                      :class="{ 'bg-secondary': !statsDataLoaded }"
                    >
                      {{ (statsData.orderStatus && statsData.orderStatus.others) || 0 }} ({{ (statsData.orderStatus && statsData.orderStatus.othersPercent) || 0 }}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Panel 2: Active Accounts / Client Status -->
        <div v-if="!fromClient" class="col-md-2 col-lg mb-3" style="flex: 0 0 16.66%; max-width: 16.66%;">
          <div class="card shadow-sm h-100" :class="{ 'position-relative': loadingStats, 'opacity-50 pe-none': !statsDataLoaded }">
            <div class="card-body">
              <h6 class="card-title font-weight-bold">Active Accounts / Client Status</h6>
              <div v-if="loadingStats" class="text-center py-3">
                <span class="spinner-border spinner-border-sm text-primary"></span>
              </div>
              <div v-else>
                <div class="mb-3">
                  <div class="d-flex align-items-center mb-2">
                    <span class="badge badge-success mr-2" :class="{ 'bg-secondary': !statsDataLoaded }">In Active Clients</span>
                    <div>
                    <h4 class="mb-0" :class="{ 'text-muted': !statsDataLoaded }">
                      {{ (statsData.clientStatus && statsData.clientStatus.active) || 0 }}
                    </h4>
                  </div>
                  </div>
                </div>
                <div class="mb-3">
                  <div class="d-flex align-items-center mb-2">
                    <span class="badge badge-warning mr-2" :class="{ 'bg-secondary': !statsDataLoaded }">In Preparing Clients</span>
                  </div>
                  <div class="ml-3">
                    <div class="small mb-1" :class="{ 'text-muted': !statsDataLoaded }">
                      Preparation: {{ (statsData.clientStatus && statsData.clientStatus.preparing && statsData.clientStatus.preparing.preparation) || 0 }}
                    </div>
                    <div class="small" :class="{ 'text-muted': !statsDataLoaded }">
                      Validation: {{ (statsData.clientStatus && statsData.clientStatus.preparing && statsData.clientStatus.preparing.validation) || 0 }}
                    </div>
                  </div>
                </div>
                <div>
                  <div class="d-flex align-items-center mb-2">
                    <span class="badge badge-danger mr-2" :class="{ 'bg-secondary': !statsDataLoaded }">In Problematic Clients</span>
                  </div>
                  <div class="ml-3">
                    <div class="small mb-1" :class="{ 'text-muted': !statsDataLoaded }">
                      Standby: {{ (statsData.clientStatus && statsData.clientStatus.problematic && statsData.clientStatus.problematic.standby) || 0 }}
                    </div>
                    <div class="small mb-1" :class="{ 'text-muted': !statsDataLoaded }">
                      Payment declined: {{ (statsData.clientStatus && statsData.clientStatus.problematic && statsData.clientStatus.problematic.paymentDeclined) || 0 }}
                    </div>
                    <div class="small mb-1" :class="{ 'text-muted': !statsDataLoaded }">
                      Recovery: {{ (statsData.clientStatus && statsData.clientStatus.problematic && statsData.clientStatus.problematic.recovery) || 0 }}
                    </div>
                    <div class="small" :class="{ 'text-muted': !statsDataLoaded }">
                      Deleted: {{ (statsData.clientStatus && statsData.clientStatus.problematic && statsData.clientStatus.problematic.deleted) || 0 }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Panel 3: Active Accounts / Provider -->
        <div v-if="!fromClient" class="col-md-4 col-lg mb-3" style="flex: 0 0 16.66%; max-width: 16.66%;">
          <div class="card shadow-sm h-100" :class="{ 'position-relative': loadingStats, 'opacity-50 pe-none': !statsDataLoaded }">
            <div class="card-body">
              <h6 class="card-title font-weight-bold">Active Accounts / Provider</h6>
              <div v-if="loadingStats" class="text-center py-3">
                <span class="spinner-border spinner-border-sm text-primary"></span>
              </div>
              <div v-else>
                <div v-if="statsData.providers && statsData.providers.length > 0">
                  <div 
                    v-for="provider in statsData.providers" 
                    :key="provider.name"
                    class="mb-2"
                    :class="{ 'text-muted': !statsDataLoaded }"
                  >
                    <strong>{{ provider.name }}:</strong> 
                    {{ provider.count || 0 }} ({{ provider.percent || 0 }}%) - 
                    {{ provider.workspaces || 0 }}  - 
                    {{ formatCurrency(provider.amount || 0) }} €
                  </div>
                  <hr>
                  <div class="font-weight-bold" :class="{ 'text-muted': !statsDataLoaded }">
                    Total: {{ (statsData.providersTotal && statsData.providersTotal.count) || 0 }} ({{ (statsData.providersTotal && statsData.providersTotal.percent) || 0 }}%) - 
                    {{ formatCurrency((statsData.providersTotal && statsData.providersTotal.amount) || 0) }} €
                  </div>
                </div>
                <div v-else class="text-muted">
                  No provider data available
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Panel 4: Active Accounts preparation time -->
        <div v-if="!fromClient" class="col-md-2 col-lg mb-3" style="flex: 0 0 16.66%; max-width: 16.66%;">
          <div class="card shadow-sm h-100" :class="{ 'position-relative': loadingStats, 'opacity-50 pe-none': !statsDataLoaded }">
            <div class="card-body">
              <h6 class="card-title font-weight-bold">Active Accounts preparation time</h6>
              <div v-if="loadingStats" class="text-center py-3">
                <span class="spinner-border spinner-border-sm text-primary"></span>
              </div>
              <div v-else>
                <h2 class="mb-3" :class="{ 'text-muted': !statsDataLoaded }">
                  {{ (statsData.preparationTime && statsData.preparationTime.total) || 0 }}
                </h2>
                <div class="d-flex flex-column align-items-start">
                  <span
                    class="badge badge-success px-2 py-1 my-2"
                    :class="{ 'bg-secondary': !statsDataLoaded }"
                  >
                    1-6 days: {{ (statsData.preparationTime && statsData.preparationTime.days1to6) || 0 }} - {{ statsData.preparationTime.days1to6Per }} %
                  </span>
                  <span 
                    class="badge badge-success px-2 py-1 mx-1 my-2"
                    :class="{ 'bg-secondary': !statsDataLoaded }"
                  >
                    7-15 days: {{ (statsData.preparationTime && statsData.preparationTime.days7to15) || 0 }} - {{ statsData.preparationTime.days7to15Per }} %
                  </span>
                  <span 
                    class="badge badge-danger px-2 py-1 mx-1 my-2"
                    :class="{ 'bg-secondary': !statsDataLoaded }"
                  >
                    16-30 days: {{ (statsData.preparationTime && statsData.preparationTime.days16to30) || 0 }} - {{ statsData.preparationTime.days16to30Per }} %
                  </span>
                  <span 
                    class="badge badge-danger px-2 py-1 mx-1 my-2"
                    :class="{ 'bg-secondary': !statsDataLoaded }"
                  >
                    31-45 days: {{ (statsData.preparationTime && statsData.preparationTime.days31to45) || 0 }} - {{ statsData.preparationTime.days31to45Per }} %
                  </span>
                  <span 
                    class="badge px-2 py-1 mx-1 my-2"
                    :class="statsDataLoaded ? 'text-white' : 'bg-secondary'"
                    :style="statsDataLoaded ? 'background-color: #9c27b0;' : ''"
                  >
                    +45 days: {{ (statsData.preparationTime && statsData.preparationTime.daysOver45) || 0 }} - {{ statsData.preparationTime.daysOver45Per }} %
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Panel 5: Active Accounts without order -->
        <div v-if="!fromClient" class="col-md-2 col-lg mb-3" style="flex: 0 0 16.66%; max-width: 16.66%;">
          <div class="card shadow-sm h-100" :class="{ 'position-relative': loadingStats, 'opacity-50 pe-none': !statsDataLoaded }">
            <div class="card-body">
              <h6 class="card-title font-weight-bold">Active Accounts without order (not prospecting)</h6>
              <div v-if="loadingStats" class="text-center py-3">
                <span class="spinner-border spinner-border-sm text-primary"></span>
              </div>
              <div v-else>
                <h2 class="mb-3" :class="{ 'text-muted': !statsDataLoaded }">
                  {{ (statsData.nonProspectingDays && statsData.nonProspectingDays.total) || 0 }}
                </h2>
                <div class="d-flex flex-column align-items-start">
                  <span
                    class="badge badge-success px-2 py-1 my-2"
                    :class="{ 'bg-secondary': !statsDataLoaded }"
                  >
                    1-6 days: {{ (statsData.nonProspectingDays && statsData.nonProspectingDays.days1to6) || 0 }} - {{ (statsData.nonProspectingDays && statsData.nonProspectingDays.days1to6Per) || 0 }} %
                  </span>
                  <span 
                    class="badge badge-success px-2 py-1 mx-1 my-2"
                    :class="{ 'bg-secondary': !statsDataLoaded }"
                  >
                    7-15 days: {{ (statsData.nonProspectingDays && statsData.nonProspectingDays.days7to15) || 0 }} - {{ (statsData.nonProspectingDays && statsData.nonProspectingDays.days7to15Per) || 0 }} %
                  </span>
                  <span 
                    class="badge badge-danger px-2 py-1 mx-1 my-2"
                    :class="{ 'bg-secondary': !statsDataLoaded }"
                  >
                    16-30 days: {{ (statsData.nonProspectingDays && statsData.nonProspectingDays.days16to30) || 0 }} - {{ (statsData.nonProspectingDays && statsData.nonProspectingDays.days16to30Per) || 0 }} %
                  </span>
                  <span 
                    class="badge badge-danger px-2 py-1 mx-1 my-2"
                    :class="{ 'bg-secondary': !statsDataLoaded }"
                  >
                    31-45 days: {{ (statsData.nonProspectingDays && statsData.nonProspectingDays.days31to45) || 0 }} - {{ (statsData.nonProspectingDays && statsData.nonProspectingDays.days31to45Per) || 0 }} %
                  </span>
                  <span 
                    class="badge px-2 py-1 mx-1 my-2"
                    :class="statsDataLoaded ? 'text-white' : 'bg-secondary'"
                    :style="statsDataLoaded ? 'background-color: #9c27b0;' : ''"
                  >
                    +45 days: {{ (statsData.nonProspectingDays && statsData.nonProspectingDays.days45Plus) || 0 }} - {{ (statsData.nonProspectingDays && statsData.nonProspectingDays.days45PlusPer) || 0 }} %
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Panel: Warmup Sync & Warmup Activation -->
        <div v-if="!fromClient" class="col-md-2 col-lg mb-3" style="flex: 0 0 16.66%; max-width: 16.66%;">
          <div class="card shadow-sm h-100" :class="{ 'position-relative': loadingStats, 'opacity-50 pe-none': !statsDataLoaded }">
            <div class="card-body">
              <div v-if="loadingStats" class="text-center py-3">
                <span class="spinner-border spinner-border-sm text-primary"></span>
              </div>
              <div v-else>
                <!-- Warmup Sync -->
                <h6 class="card-title font-weight-bold mb-2">Warmup Sync</h6>
                <div class="d-flex flex-column align-items-start mb-3">
                  <span
                    class="badge badge-success px-2 py-1 my-1 text-white"
                    :class="{ 'bg-secondary': !statsDataLoaded }"
                  >
                    Synced = {{ (statsData.warmupSync && statsData.warmupSync.synced) || 0 }} ({{ (statsData.warmupSync && statsData.warmupSync.syncedPercent) || 0 }}%)
                  </span>
                  <span
                    class="badge badge-danger px-2 py-1 my-1 text-white"
                    :class="{ 'bg-secondary': !statsDataLoaded }"
                  >
                    Not Synced = {{ (statsData.warmupSync && statsData.warmupSync.notSynced) || 0 }} ({{ (statsData.warmupSync && statsData.warmupSync.notSyncedPercent) || 0 }}%)
                  </span>
                </div>
                <!-- Barra divisoria -->
                <hr class="my-3 border-secondary" />
                <!-- Warmup Activation -->
                <h6 class="card-title font-weight-bold mb-2">Warmup Activation</h6>
                <div class="d-flex flex-column align-items-start">
                  <span
                    class="badge badge-success px-2 py-1 my-1 text-white"
                    :class="{ 'bg-secondary': !statsDataLoaded }"
                  >
                    Active = {{ (statsData.warmupActivation && statsData.warmupActivation.active) || 0 }} ({{ (statsData.warmupActivation && statsData.warmupActivation.activePercent) || 0 }}%)
                  </span>
                  <span
                    class="badge badge-danger px-2 py-1 my-1 text-white"
                    :class="{ 'bg-secondary': !statsDataLoaded }"
                  >
                    Inactive = {{ (statsData.warmupActivation && statsData.warmupActivation.inactive) || 0 }} ({{ (statsData.warmupActivation && statsData.warmupActivation.inactivePercent) || 0 }}%)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
       
    <div class="mx-2">
      <LoadingSpinner v-if="loading" />
    </div>
    <div class="mx-2" v-if="!loading">
      <Modal :name="'addEmail'" :close="true">
        <AddEmailWorkspace
          :user_id="addEmailUserId"
          @done="done()"
        ></AddEmailWorkspace>
      </Modal>
      <Modal :name="'addDomain'" :close="true">
        <AddDomainWorkspace
          :workspaces="workspace"
          :sales_representative_id="sales_rep_id"
          @done="doneDomain()"
        ></AddDomainWorkspace>
      </Modal>
      <Modal :name="'seeEmails'" :close="true">
        <LastEmails :user_id="user_id"></LastEmails>
      </Modal>
      <div class="w-100 mb-4">
        <div class="w-50 ml-2">
          <!-- <Tabs
            :tabSelected="tabSelected"
            :tabs="tabs"
            @change="changeTab($event)"
          /> -->
        </div>
      </div>

      
      <div  class="col-md-12 mb-2 card p-2" v-if="!loading && !fromClient">
        <vue-form-generator
          :schema="schemaStatus"
          :model="paginationmodel"
          :options="formOptions"
        ></vue-form-generator>
        <div class="col-md-2">
          <button
            class="btn btn-sm btn-primary mr-2"
            @click="applyFilter()"
          >
            Filtrer
          </button>
          <button class="btn btn-sm btn-primary" @click="resetFilter()">
            Reset
          </button>
        </div>
      </div>
      <div class="col-md-12">
        <div class="my-3">
          Total: {{ total }}
          <button
            v-if="!fromClient"
            class="ml-2 btn btn-sm btn-primary"
            @click="getToDownload()"
            :disabled="processingAllItems"
          >
            <span v-if="!processingAllItems"> Download csv </span>
            <span v-else>Processing...</span>
          </button>
        </div>
        <div class="card scrollable">
          <Table
            v-if="accountemails.length > 0"
            :headers="headers"
            :cols="cols"
            :data="accountemails"
            :checkbox="selectcheckbox"
            :notselect="false"
            :model="model"
            :allselected="false"
            @createEmail="createEmail"
            @createDomain="createDomain"
            @seeEmails="seeEmails"
            @deleteUser="deleteUser"
            @deleteMailbox="deleteMailbox"
            @updateModel="updateModel($event)"
            @openClient="openClient"
            @openTarget="openTarget"
          >
          </Table>
        </div>

        <button
          class="btn btn-sm btn-danger"
          @click="deleteUser"
          v-if="selectcheckbox && !fromClient"
          :disabled="deleting"
        >
          Delete
        </button>
        <span
          v-if="deleting"
          class="loadingres mr-1 ml-1 spinner-border loading-spinner text-primary"
        ></span>
      </div>
    </div>
    <div class="row mt-4" v-if="accountemails.length > 0">
      <div class="col">
        <Pagination
          v-if="!loading"
          :total="total"
          :totalpage="accountemails.length"
          :model="paginationmodel"
          :limit="false"
          :color="'primary'"
          @changePage="changePage"
          key="323323"
        ></Pagination>
      </div>
    </div>

    <DeleteDomains
      v-if="false && !loading && workspace_domain.length > 0"
      @refresh="getAccountEmails()"
      :workspace_domain="workspace_domain"
      :key="keydomains"
    ></DeleteDomains>
  </div>
</template>
<script>
import Table from "@/components/Common/Table.vue";
import Modal from "@/components/Common/Modal.vue";
import AddEmailWorkspace from "@/components/Workspace/AddEmailWorkspace.vue";
import AddDomainWorkspace from "@/components/Workspace/AddDomainWorkspace.vue";
import DeleteDomains from "@/components/Workspace/DeleteDomains.vue";
import LastEmails from "@/components/Emails/LastEmails.vue";
import {
  monitorService,
  workspaceService,
  usersService,
} from "@/_services";
import VueFormGenerator from "vue-form-generator";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Pagination from "@/components/Common/Pagination.vue";
import Tabs from "@/components/Common/Tabs.vue";
import moment from "moment";

export default {
  props: {
    fromClient: {
      type: Boolean,
      default: false
    },
    clientId: {
      type: [Number, String],
      default: null
    },
  },
  data() {
    return {
      processingAllItems: false,
      deleting: false,
      deletingMailboxIds: [],
      user_id: false,
      selectcheckbox: false,
      keybuttondelete: 323432,
      keydomains: 23,
      sales_rep_id: false,
      addEmailUserId: false,
      filters: [],
      workspace_domain: [],
      workspace_domain_aux: [],
      workspace_domain_id: false,
      total: false,
      accountemails: [],
      loading: true,
      model: {},
      paginationmodel: {
        currentPage: 1,
        cantPerPage: 100,
      },
      cols: [],
      headers: [],
      clients: [],
      client_aux: [],
      client_status: [],
      client_status_aux: [],
      workspace_aux: [],
      workspace: [],
      sales: [],
      filterYearMonth: moment().format("YYYY-MM"),
      provider: [],
      provider_aux: [],
      user_status: [],
      user_status_aux: [],
      tabSelected: 1,
      tabs: {
        1: {
          id: 1,
          name: "All Mailboxes",
        },        
        2: {
          id: 2,
          name:  "Pending domain purchase",
        },
        3: {
          id: 3,
          name: "Pending domain config",
        },
        4: {
          id: 4,
          name: "Pending mailbox purchase",
        },
        5: {
          id: 5,
          name: "Pending Oliverlist sync",
        },
        6: {
          id: 6,
          name: "Pending Smartlead sync",
        },
        7: {
          id: 7,
          name: "Pending warmup activation",
        },
        8: {
          id: 8,
          name: "Pending deletion",
        },
      },
      schemaStatus: {
        fields: [
          {
            type: "select",
            inputType: "text",
            label: "Client",
            model: "client_id",
            readonly: false,
            required: true,
            disabled: false,
            selectOptions: {},
            values: () => {
              return this.clients;
            },
            styleClasses: "col-sm-6 col-md-3",
          },
          {
            type: "select",
            inputType: "text",
            label: "Client Status",
            model: "client_status_id",
            readonly: false,
            required: true,
            disabled: false,
            selectOptions: {},
            values: () => {
              return this.client_status;
            },
            styleClasses: "col-sm-6 col-md-3",
          },
          {
            type: "select",
            inputType: "text",
            label: "User Status",
            model: "user_status_id",
            readonly: false,
            required: false,
            disabled: false,
            selectOptions: {},
            values: () => {
              return this.user_status;
            },
            styleClasses: "col-sm-6 col-md-3",
          },
          {
            type: "select",
            inputType: "text",
            label: "Provider",
            model: "provider",
            readonly: false,
            required: true,
            disabled: false,
            selectOptions: {},
            values: () => {
              return this.provider;
            },
            styleClasses: "col-sm-6 col-md-3",
          },
          {
            type: "select",
            inputType: "text",
            label: "Workspace",
            model: "workspace_id",
            readonly: false,
            required: true,
            disabled: false,
            selectOptions: {},
            values: () => {
              return this.workspace;
            },
            styleClasses: "col-sm-6 col-md-3",
          },
          {
            type: "input",
            inputType: "text",
            model: "email",
            placeholder: "ex : alice.lac@ateliersouchamp.com",
            featured: true,
            required: true,
            label: this.$t("lang.label.email"),
            styleClasses: "col-md-3",
          },
          // {
          //   type: "checkbox",
          //   label: "Not registered in nylas",
          //   model: "nylas_notsync",
          //   featured: false,
          //   styleClasses: "col-md-3",
          // },
          {
            type: "checkbox",
            label: "Disconnected from Oliverlist",
            model: "nylas_disconnected",
            featured: false,
            styleClasses: "col-md-3",
          },
          // {
          //   type: "checkbox",
          //   label: "No Dkim",
          //   model: "dkim",
          //   featured: false,
          //   styleClasses: "col-md-3",
          // },
          {
            type: "select",
            inputType: "text",
            label: this.$t("lang.label.order"),
            model: "hastarget",
            readonly: false,
            required: true,
            disabled: false,
            selectOptions: {},
            values: () => {
              return [
                { id: "", name: "All" },
                { id: 1, name: "Preparation" },
                { id: 2, name: "Stop" },
                { id: 3, name: "Draft" },
                { id: 4, name: "Running" },
                { id: 5, name: "Deleted" },
                { id: 6, name: "Paused" },
                { id: "yes", name: "Yes" },
                { id: "no", name: "No" },
              ];
            },
            styleClasses: "col-sm-6 col-md-3",
          },
          {
            type: "input",
            inputType: "number",
            label: "Non prospecting email (more than)",
            model: "non_prospecting_days",
            readonly: false,
            required: true,
            disabled: false,
            min: 0,
            selectOptions: {},
            value: 0,
            styleClasses: "col-md-3",
          },
          {
            type: "select",
            inputType: "text",
            label: "Warmup",
            model: "sl_warmup_status",
            readonly: false,
            required: true,
            disabled: false,
            selectOptions: {},
            values: () => {
              return [
                { id: '', name: "All" },
                { id: 'active', name: "Active" },
                { id: 'inactive', name: "Inactive" },

              ];
            },
            styleClasses: "col-sm-6 col-md-3",
          },
          {
            type: "select",
            inputType: "text",
            label: "Account Manager",
            model: "am_user_id",
            readonly: false,
            required: true,
            disabled: false,
            selectOptions: { name: "label", id: "id" },
            values: () => {
              return this.sales;
            },
            styleClasses: "col-sm-6 col-md-3",
          },
          // {
          //   type: "checkbox",
          //   label: "No Https",
          //   model: "https",
          //   featured: false,
          //   styleClasses: "col-md-3",
          // },
          // {
          //   type: "checkbox",
          //   label: "No Workspace",
          //   model: "workspace",
          //   featured: false,
          //   styleClasses: "col-md-3",
          // },
          // {
          //   type: "checkbox",
          //   label: "With domain",
          //   model: "domain",
          //   featured: false,
          //   styleClasses: "col-md-3",
          // },
        ],
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
      },
      accountemailsAllItems: [],
      loadingStats: false,
      statsDataLoaded: false,
      statsData: {
        totalActiveAccounts: 0,
        orderStatus: {
          active: 0,
          activePercent: 0,
          preparing: 0,
          preparingPercent: 0,
          problematic: 0,
          problematicPercent: 0,
        },
        clientStatus: {
          active: 0,
          preparing: {
            preparation: 0,
            validation: 0,
          },
          problematic: {
            standby: 0,
            paymentDeclined: 0,
            recovery: 0,
            deleted: 0,
          },
        },
        providers: [],
        getTotal: 0,
        providersTotal: {
          count: 0,
          percent: 0,
          amount: 0,
        },
        preparationTime: {
          total: 0,
          days1to6: 0,
          days7to15: 0,
          days16to30: 0,
          days31to45: 0,
          daysOver45: 0,
        },
        nonProspectingDays: {
          total: 0,
          days1to6: 0,
          days7to15: 0,
          days16to30: 0,
          days31to45: 0,
          days45Plus: 0,
          days1to6Per: 0,
          days7to15Per: 0,
          days16to30Per: 0,
          days31to45Per: 0,
          days45PlusPer: 0,
        },
        warmupSync: {
          synced: 0,
          syncedPercent: 0,
          notSynced: 0,
          notSyncedPercent: 0,
        },
        warmupActivation: {
          active: 0,
          activePercent: 0,
          inactive: 0,
          inactivePercent: 0,
        },
      },
    };
  },
  components: {
    Table,
    "vue-form-generator": VueFormGenerator.component,
    LoadingSpinner,
    Pagination,
    Modal,
    LastEmails,
    AddEmailWorkspace,
    AddDomainWorkspace,
    DeleteDomains,
    Tabs,
  },

  computed: {
    user() {
      return this.$store.state.authentication.user;
    },
    isAdmin() {
      return !!this.user && this.user.role_id == 1;
    },
  },

  created() {
    this.cols = [
    [
      {
        value_label: 'client_name',
        value_emit: 'client_id',
        class: 'text-primary cursor-pointer',
        type: 'emit',
        emit: 'openClient',
      },
      ],
      [
        {
          value: "client_status",
          type: "itemBadge",
          class: "badge badge-pill text-small",
          colors: [
            {
              id: "Active",
              label: "Active",
              bgColor: "green",
              textColor: "#FFFFFF",
            },
            {
              id: "Standby",
              label: "Standby",
              bgColor: "gray",
              textColor: "#FFFFFF",
            },
            {
              id: "Deleted",
              label: "Deleted",
              bgColor: "red",
              textColor: "#FFFFFF",
            },
            {
              id: "Preparing",
              label: "Preparing",
              bgColor: "yellow",
              textColor: "#000000",
            },
          ],
        },
      ],
      [
        {
          value: "target_ids",
        },
      ],     
      [
        {
          value_label: "running_order",
          value_emit: "running_order",
          class: "text-primary",
          textShort: 40,
          type: "emitConditional",
          emit: "openTarget",
        },
      ],
      [
        {
          // Domain request (fecha)
          value: "domain_requested_date", 

        },
      ],
      [
        {
          // Domain 
          value: "domain", 
        },
      ],
      [
        {
          // Domain Status (Pending/Purchased)
          value: "domain", 
          type: "btnIfNotNull",
          format: 1,
          label: "Purchased",
          default: "Pending",
        },
      ],
      [
        {
          // Domain purchase (fecha)
          value: "domain_created", 
        },
      ],
      [
        {
          // Domain DNS (check / cruz)
          value: "domain_dns", 
        },
      ],
      [
        {
          value: "provider",
        },
      ],
      [
        {
          value: "workspace",
        },
      ],
      [
        {
          value: "team_user_name",
        }
      ],
      [
        {
          value: "email",
        },
      ],
      [
        {
          value: "user_status",
          type: "itemBadge",
          class: "badge badge-pill text-small",
          colors: [
            {
              id: "active",
              label: "Active",
              bgColor: "green",
              textColor: "#FFFFFF",
            },
            {
              id: "inactive",
              label: "Inactive",
              bgColor: "gray",
              textColor: "#FFFFFF",
            },
            {
              id: "delete",
              label: "Deleted",
              bgColor: "red",
              textColor: "#FFFFFF",
            },
            {
              id: "pending-workspace",
              label: "Pending",
              bgColor: "yellow",
              textColor: "#000000",
            },
            {
              id: "waiting-replace",
              label: "Waiting Replace",
              bgColor: "yellow",
              textColor: "#FFFFFF",
            },
          ],
        },
      ],
      [
        {
          value: "wu_created",
        },
      ],
      [
        {
          // Mailbox Synced to Oliverlist (Check/Cruz)
          value: "cn_sync",
          show_text: "false",
          icons: [
            {
              icon_if_value: "1",
              icon_true: "icon-checkmark text-success"
            },
            {
              icon_if_value: "0",
              icon_true: "icon-cross text-danger"
            }
          ]
        },
      ],
      [
        {
          // Mailbox Synced to Smartlead (Check/Cruz)
          value: "sl_sync",
          show_text: "false",
          icons: [
            {
              icon_if_value: "1",
              icon_true: "icon-checkmark text-success"
            },
            {
              icon_if_value: "0",
              icon_true: "icon-cross text-danger"
            }
          ]
        },
      ],
      [
        {
          // Warmup status (activo / inactivo / nulo)
          value: "sl_warmup_status",  
          type: "itemBadge",
          class: "badge badge-pill text-small",
          colors: [
            {
              id: "Active",
              label: "Active",
              bgColor: "green",
              textColor: "#FFFFFF",
            },
            {
              id: "Inactive",
              label: "Inactive",
              bgColor: "gray",
              textColor: "#FFFFFF",
            },
          ],
        },
      ],
      [
        {
          value: "non_prospecting_days"
        },
      ],
      [
        {
          // Baja de un solo buzon (Zapmail + Oliverlist), solo admin
          type: "btnAction",
          name: "delete_mailbox_label",
          value: "user_id",
          btnId: "user_id",
          emit: "deleteMailbox",
          visible_value: "zm_deletable",
          disabled: [],
          loading_data: this.deletingMailboxIds,
        },
      ],
    ];
    this.headers = [
      // Domain request
      {
        label: "Client",
        size: "130",
        class: "overflowhide",
      },
      {
        label: "Client status",
        size: "130",
        class: "overflowhide",
      },
      {
        label: this.$t("lang.orders.orders"),
        size: "140px",
        class: "overflowhide",
      },
      {
        label: "Running order",
        size: "140px",
        class: "overflowhide",
      },
      {
        label: "Domain request",
        size: "130",
        class: "overflowhide",
      },
      // Domain
      {
        label: "Domain",
        size: "130",
        class: "overflowhide",
      },
      // Domain Status
      {
        label: "Domain Status",
        size: "130",
        class: "overflowhide",
      },
      // Domain purchase
      {
        label: "Domain purchase",
        size: "130",
        class: "overflowhide",
      },
      // Domain DNS
      {
        label: "Domain DNS",
        size: "130",
        class: "overflowhide",
      },
      {
        label: "Provider",
        size: "130",
        class: "overflowhide",
      },
      {
        label: "Workspace",
        size: "100",
        class: "overflowhide",
      },
      {
        label: "Team",
        size: "100",
        class: "overflowhide",
      },
      {
        label: "Mailbox",
        size: "330px",
        class: "overflowhide",
      },
      {
        label: "Mailbox status",
        size: "130",
        class: "overflowhide",
      },
      {
        label: "Mailbox creation date",
        size: "140px",
        class: "overflowhide",
      },
      // Mailbox Synced to Oliverlist
      {
        label: "Mailbox Synced to Oliverlist",
        size: "130",
        class: "overflowhide",
      },
      // Mailbox Synced to Smartlead
      {
        label: "Mailbox Synced to Smartlead",
        size: "130",
        class: "overflowhide",
      },
      //Warmup status
      {
        label: "Warmup status",
        size: "130",
        class: "overflowhide",
      },
      {
        label: "Non prospecting days",
        size: "130",
        class: "overflowhide",
      },      {
        label: "Actions",
        size: "130",
        class: "overflowhide",
      },
    ];
    if(!this.fromClient){
      const teamColIndex = 11;
      this.cols.splice(teamColIndex, 1);
      this.headers.splice(teamColIndex, 1);

      this.getAccountEmailsStats();
      this.getAccountEmails(0, false);
      this.getAccountEmails(0, true);
      this.getUserOliverSales(null, this.filterYearMonth);
      this.getFilter();
    }else{
      this.getAccountEmails(0, false);
      this.getAccountEmails(0, true);
    }
  },
  methods: {
    changeTab(tabId) {
      this.tabSelected = tabId;
      this.paginationmodel.currentPage = 1;
      this.getAccountEmails();
    },
    getUserOliverSales(month){
      usersService.getUserOliverSales(true, month, true)
      .then((response) => {
        if (response.result.length > 0) {
          this.sales = [
            { id: "0", label: "Select option..." },
            { id: "all", label: "All" },
          ];
          const userOliverSalesWithlabel = response.result.map((el) => ({
            ...el,
            label: el.lastname + ", " + el.name,
          }));
          this.sales.push(...userOliverSalesWithlabel);
        }
      })
    },
    openClient(clientId) {
        const url = `/clients/${clientId}`;
        window.open(url, '_blank');
    },
    openTarget(order_id) {
        let url = "/orders/" + order_id;
        window.open(url, "_blank");
    },
    getToDownload() {
      const allItems = 1; // cuando quieres pedir todos los resultados sin limite
      this.getAccountEmails(allItems, false);
    },
    download() {
      const nameDocument = "report-mailbox-history.csv";

      //Tomo headers desde simpleheader
      const exportIdx = this.cols
        .map((c, i) => (c[0].type === "btnAction" ? -1 : i))
        .filter((i) => i >= 0);
      const headers = exportIdx.map((i) => this.headers[i].label);

      const fields = exportIdx.map((i) => this.cols[i]).map((item) => {
        const value = item[0].value ? item[0].value : item[0].value_label;
        return value;
      });

      const rows = this.accountemailsAllItems.map((item) => {
        return fields
          .map((col) => {
            return item[col];
          })
          .map(
            (v) =>
              `"${String(v !== undefined && v !== null ? v : "").replace(
                /"/g,
                '""'
              )}"`
          )
          .join(",");
      });

      const csv = `${headers.join(",")}\n${rows.join("\n")}`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", nameDocument);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    deleteUser(user_id) {
      if (this.model.selectedIds.length > 0) {
        let data = {
          users: this.model.selectedIds,
          fullDeleteDB: true,
        };
        this.deleting = true;
        usersService.deleteUsers(data).then((r) => {
          this.getAccountEmails(0, false);
          this.getAccountEmails(0, true);
          this.model.selectedIds = [];
          this.deleting = false;
        });
      }
    },
    deleteMailbox(userId) {
      const row = this.accountemails.find((r) => r.user_id == userId);
      if (!row || this.deletingMailboxIds.includes(userId)) return;
      if (
        !window.confirm(
          "Delete mailbox " +
            row.email +
            " in ZapMail and Oliverlist? If it is the last ZapMail mailbox of " +
            row.domain +
            ", ZapMail removes the whole domain."
        )
      ) {
        return;
      }
      this.deletingMailboxIds.push(userId);
      workspaceService.deleteZapmailMailbox(userId).then(
        () => {
          this.deletingMailboxIds.splice(this.deletingMailboxIds.indexOf(userId), 1);
          this.getAccountEmails(0, false);
          this.getAccountEmails(0, true);
        },
        (error) => {
          this.deletingMailboxIds.splice(this.deletingMailboxIds.indexOf(userId), 1);
          this.$store.dispatch("alert/error", error, { root: true });
        }
      );
    },
    updateModel(mod) {
      this.model = mod;
    },
    getAccountEmails(allItems = 0, total = false) {
      // If only getting total, call getAccountEmailsTotal and return early
      if (total && allItems == 0) {
        this.getAccountEmailsTotal();
        return;
      }

      if(allItems == 1 && total == false){
        this.paginationmodel.allItems = allItems;
      }else{
        this.paginationmodel.allItems = 0;
      }
      
      if (allItems == 0) {
        this.loading = true;
      } else {
        this.processingAllItems = true;
      }

      this.selectcheckbox = false;

      // Create a clean copy of paginationmodel without empty values (for "All" selections)
      const cleanParams = {};
      for (const key in this.paginationmodel) {
        if (this.paginationmodel[key] !== "" && this.paginationmodel[key] !== null && this.paginationmodel[key] !== undefined) {
          cleanParams[key] = this.paginationmodel[key];
        }
      }

      if(allItems == 1){
        cleanParams.allItems = 1;
        cleanParams.getTotal = 0;
      }

      // si mailbox se muestra desde vista del cliente
      if(this.fromClient){
        cleanParams.client_id = this.clientId;
        cleanParams.fromClient = true;
      }
      
      monitorService.getAccountEmails(cleanParams).then((result) => {
        if (allItems == 1) {
          this.accountemailsAllItems = result.data;
          this.download();
          this.processingAllItems = false;
        } else {
          this.accountemails = result.data;
          this.accountemails.forEach((item) => {
            if(item.email_owner == null) item.email_owner = '-';
            if(item.provider == null) item.provider = '-';
            item.zm_deletable =
              this.isAdmin &&
              typeof item.workspace === "string" &&
              item.workspace.startsWith("ZM-") &&
              item.user_status !== "delete";
            item.delete_mailbox_label = "Delete";
          });
          this.loading = false;

          if (this.paginationmodel.client_status_id == 6) {
            this.workspace_domain = [];
            this.workspace_domain_aux = [];
            result.forEach((r) => {
              if (
                r.workspace_domain_id !== null &&
                !this.workspace_domain_aux.includes(r.workspace_domain_id)
              ) {
                this.workspace_domain_aux.push(r.workspace_domain_id);

                this.workspace_domain.push({
                  id: r.workspace_domain_id,
                  domain: r.domain,
                });
              }
            });
          } else {
            if (
              this.paginationmodel.user_status_id == 3 ||
              this.paginationmodel.user_status_id == 5
            ) {
              this.selectcheckbox = true;
            }

            this.workspace_domain = [];
            if (this.paginationmodel.client_id) {
              workspaceService
                .getFreeDomainsByClient(this.client_id)
                .then((result) => {
                  this.workspace_domain = result;
                  this.loading = false;
                });
            } else {
              workspaceService.getFreeDomainsByClient(false).then((result) => {
                this.workspace_domain = result;
                this.loading = false;
              });
            }
          }
        }
      });

      //     this.getAccountEmailsTotal()
    },
    getAccountEmailsTotal() {
      // Create a clean copy of paginationmodel without empty values (for "All" selections)
      const cleanParams = {};
      for (const key in this.paginationmodel) {
        if (this.paginationmodel[key] !== "" && this.paginationmodel[key] !== null && this.paginationmodel[key] !== undefined) {
          cleanParams[key] = this.paginationmodel[key];
        }
      }
      // Set getTotal to 1 and ensure allItems is 0 for total count query
      cleanParams.getTotal = 1;
      cleanParams.allItems = 0;

      if(this.fromClient){
        cleanParams.client_id = this.clientId;
        cleanParams.fromClient = true;
      }
      monitorService
        .getAccountEmails(cleanParams)
        .then((result) => {
          this.total = result.total;
        });
    },
    changePage() {
      this.getAccountEmails(0, false);
    },
    createEmail(user_id) {
      this.addEmailUserId = user_id;
      this.$modal.show("addEmail");
    },
    createDomain(sales_representative_id) {
      this.sales_rep_id = sales_representative_id;
      this.$modal.show("addDomain");
    },
    seeEmails(user_id) {
      this.user_id = user_id;
      this.$modal.show("seeEmails");
    },
    done() {
      this.addEmailUserId = false;
      this.$modal.hide("addEmail");
      this.getAccountEmails(0, false);
      this.getAccountEmails(0,true);
    },
    doneDomain() {
      this.sales_rep_id = false;
      this.$modal.hide("addDomain");
      this.getAccountEmails(0, false);
      this.getAccountEmails(0,true);
    },
    applyFilter() {
      // Reset to first page when applying filters
      this.paginationmodel.currentPage = 1;
      this.paginationmodel.allItems = 0;
      this.getAccountEmails(0, false);
      this.getAccountEmails(0,true);
    },
    resetFilter() {
      this.paginationmodel = {
        currentPage: 1,
        cantPerPage: 100,
      };
      this.getAccountEmails(0, false);
      this.getAccountEmails(0,true);
      // Stats will be loaded automatically after getAccountEmails completes
    },
    getFilter() {
      monitorService.getAccountEmailsFilters().then((result) => {
        // Add "All" option at the beginning of each filter array to allow deselection
        this.clients.push({ id: "", name: "All" });
        this.client_status.push({ id: "", name: "All" });
        this.user_status.push({ id: "", name: "All" });
        this.provider.push({ id: "", name: "All" });
        this.workspace.push({ id: "", name: "All" });

        result.forEach((r) => {
          if (!this.client_aux.includes(r.client_id)) {
            this.client_aux.push(r.client_id);
            this.clients.push({
              id: r.client_id,
              name: r.client_name,
            });
          }
          if (!this.client_status_aux.includes(r.client_status_id)) {
            this.client_status_aux.push(r.client_status_id);
            this.client_status.push({
              id: r.client_status_id,
              name: r.client_status,
            });
          }
          if (!this.user_status_aux.includes(r.user_status_id)) {
            this.user_status_aux.push(r.user_status_id);
            this.user_status.push({
              id: r.user_status_id,
              name: r.user_status,
            });
          }
          if (
            r.provider !== null &&
            r.provider.length > 0 &&
            !this.provider_aux.includes(r.provider)
          ) {
            this.provider_aux.push(r.provider);
            this.provider.push({
              id: r.provider,
              name: r.provider,
            });
          }
          if (
            r.workspace_id !== null &&
            r.workspace_id.length > 0 &&
            !this.workspace_aux.includes(r.workspace_id)
          ) {
            this.workspace_aux.push(r.workspace_id);
            this.workspace.push({
              id: r.workspace_id,
              name: r.email_owner,
            });
          }
        });

        this.getOtherWorkspaces();
      });
    },

    getOtherWorkspaces(){
      workspaceService.getOtherWorkspaces().then((result) => {
        result.forEach((r) => {
          if (
            r.workspace_id !== null &&
            r.workspace_id.length > 0 &&
            !this.workspace_aux.includes(r.workspace_id)
          ) {
            this.workspace_aux.push(r.workspace_id);
            this.workspace.push({
              id: r.workspace_id,
              name: r.email_owner,
            });
          }
        })
      })
      .catch((error) => {
        console.error("Error fetching other workspaces:", error);
      });      
    },
    getAccountEmailsStats() {
      this.loadingStats = true;
      this.statsDataLoaded = false;
      
      // Reset stats data to show gray/disabled state
      this.statsData = {
        totalActiveAccounts: 0,
        orderStatus: {
          active: 0,
          activePercent: 0,
          preparing: 0,
          preparingPercent: 0,
          problematic: 0,
          problematicPercent: 0,
        },
        clientStatus: {
          active: 0,
          preparing: {
            preparation: 0,
            validation: 0,
          },
          problematic: {
            standby: 0,
            paymentDeclined: 0,
            recovery: 0,
            deleted: 0,
          },
        },
        providers: [],
        providersTotal: {
          count: 0,
          percent: 0,
          amount: 0,
        },
        preparationTime: {
          total: 0,
          days1to6: 0,
          days7to15: 0,
          days16to30: 0,
          daysOver30: 0,
          days1to6Per: 0,
          days7to15Per: 0,
          days16to30Per: 0,
          daysOver30Per: 0,
        },
        nonProspectingDays: {
          total: 0,
          days1to6: 0,
          days7to15: 0,
          days16to30: 0,
          days31to45: 0,
          days45Plus: 0,
          days1to6Per: 0,
          days7to15Per: 0,
          days16to30Per: 0,
          days31to45Per: 0,
          days45PlusPer: 0,
        },
        warmupSync: {
          synced: 0,
          syncedPercent: 0,
          notSynced: 0,
          notSyncedPercent: 0,
        },
        warmupActivation: {
          active: 0,
          activePercent: 0,
          inactive: 0,
          inactivePercent: 0,
        },
      };
      // Create a clean copy of paginationmodel without empty values (for "All" selections)
      const cleanParams = {};
      for (const key in this.paginationmodel) {
        if (this.paginationmodel[key] !== "" && this.paginationmodel[key] !== null && this.paginationmodel[key] !== undefined) {
          cleanParams[key] = this.paginationmodel[key];
        }
      }

      // Call API to get statistics
      monitorService.getAccountEmailsStats(cleanParams)
        .then((result) => {
          // Handle both cases: result.data or result directly
          const data = (result && result.data) ? result.data : result;
          
          if (data) {
            
            // Map targets to Total Active Accounts
            const targets = data.targets || {};
            const totalTargets = parseInt(targets.total || 0);
            const runningTargets = parseInt(targets.running || 0);
            const preparingTargets = parseInt(targets.preparing || 0);
            const stoppedTargets = parseInt(targets.stopped || 0);
            const othersTargets = parseInt(targets.others || 0);
            
            this.statsData.totalActiveAccounts = totalTargets;
            this.statsData.orderStatus.active = runningTargets;
            this.statsData.orderStatus.preparing = preparingTargets;
            this.statsData.orderStatus.problematic = stoppedTargets;
            this.statsData.orderStatus.others = othersTargets;
            
            // Calculate percentages
            if (totalTargets > 0) {
              this.statsData.orderStatus.activePercent = Math.floor((runningTargets / totalTargets) * 100);
              this.statsData.orderStatus.preparingPercent = Math.floor((preparingTargets / totalTargets) * 100);
              this.statsData.orderStatus.problematicPercent = Math.floor((stoppedTargets / totalTargets) * 100);
              this.statsData.orderStatus.othersPercent = Math.floor((othersTargets / totalTargets) * 100);
            }
            
            // Map clients to Active Accounts / Client Status
            const clients = data.clients || {};
            this.statsData.clientStatus.active = parseInt(clients.active_clients || 0);
            this.statsData.clientStatus.preparing.preparation = parseInt(clients.preparing_clients || 0);
            this.statsData.clientStatus.preparing.validation = parseInt(clients.validation_clients || 0);
            this.statsData.clientStatus.problematic.standby = parseInt(clients.standby_clients || 0);
            this.statsData.clientStatus.problematic.paymentDeclined = parseInt(clients.payment_declined_clients || 0);
            this.statsData.clientStatus.problematic.recovery = parseInt(clients.recovery_clients || 0);
            this.statsData.clientStatus.problematic.deleted = parseInt(clients.deleted_clients || 0);
            
            // Map email_providers to Active Accounts / Provider
            const providers = data.email_providers || {};
            const totalProviders = parseInt(providers.total || 0);
            
            this.statsData.providers = [
              {
                name: 'Zapmail',
                count: parseInt(providers.zapmail || 0),
                percent: parseInt(providers.zapmail_percent || 0),
                amount: parseFloat(providers.total_zapmail || 0),
                workspaces: parseFloat(providers.zapmail_workspaces || 0),
              },
              {
                name: 'Apog',
                count: parseInt(providers.ews || 0),
                percent: parseInt(providers.ews_percent || 0),
                amount: parseFloat(providers.total_ews || 0),
                workspaces: parseFloat(providers.apog_workspaces || 0),
              },
              {
                name: 'Google',
                count: parseInt(providers.google || 0),
                percent: parseInt(providers.google_percent || 0),
                amount: parseFloat(providers.total_google || 0),
                workspaces: parseFloat(providers.gmail_workspaces || 0),
              },
            ];
            
            this.statsData.providersTotal = {
              count: totalProviders,
              percent: 100,
              amount: parseFloat(providers.total_zapmail || 0) + 
                      parseFloat(providers.total_ews || 0) + 
                      parseFloat(providers.total_google || 0),
            };
            
            // Map owarm to Active Accounts preparation time
            const owarm = data.owarm || {};
            const totalPreparation = parseInt(owarm.total || 0);
            const days1to6Prep = parseInt(owarm.days_1_6 || 0);
            const days7to15Prep = parseInt(owarm.days_7_15 || 0);
            const days16to30Prep = parseInt(owarm.days_16_30 || 0);
            const days31to45Prep = parseInt(owarm.days_31_45 || 0);
            const daysOver45Prep = parseInt(owarm.days_45_plus || 0);
            
            this.statsData.preparationTime.total = totalPreparation;
            this.statsData.preparationTime.days1to6 = days1to6Prep;
            this.statsData.preparationTime.days7to15 = days7to15Prep;
            this.statsData.preparationTime.days16to30 = days16to30Prep;
            this.statsData.preparationTime.days31to45 = days31to45Prep;
            this.statsData.preparationTime.daysOver45 = daysOver45Prep;
            
            // Calculate percentages manually: variable / total
            if (totalPreparation > 0) {
              this.statsData.preparationTime.days1to6Per = Math.round((days1to6Prep / totalPreparation) * 100);
              this.statsData.preparationTime.days7to15Per = Math.round((days7to15Prep / totalPreparation) * 100);
              this.statsData.preparationTime.days16to30Per = Math.round((days16to30Prep / totalPreparation) * 100);
              this.statsData.preparationTime.days31to45Per = Math.round((days31to45Prep / totalPreparation) * 100);
              this.statsData.preparationTime.daysOver45Per = Math.round((daysOver45Prep / totalPreparation) * 100);
            }
            
            // Map non_prospecting_days to Active Accounts without order
            const nonProspectingDays = data.non_prospecting_days || {};
            const totalNonProspecting = parseInt(nonProspectingDays.total || 0);
            const days1to6 = parseInt(nonProspectingDays.days_1_6 || 0);
            const days7to15 = parseInt(nonProspectingDays.days_7_15 || 0);
            const days16to30 = parseInt(nonProspectingDays.days_16_30 || 0);
            const days31to45 = parseInt(nonProspectingDays.days_31_45 || 0);
            const days45Plus = parseInt(nonProspectingDays.days_45_plus || 0);
            
            this.statsData.nonProspectingDays.total = totalNonProspecting;
            this.statsData.nonProspectingDays.days1to6 = days1to6;
            this.statsData.nonProspectingDays.days7to15 = days7to15;
            this.statsData.nonProspectingDays.days16to30 = days16to30;
            this.statsData.nonProspectingDays.days31to45 = days31to45;
            this.statsData.nonProspectingDays.days45Plus = days45Plus;
            
            // Calculate percentages manually: variable / total
            if (totalNonProspecting > 0) {
              this.statsData.nonProspectingDays.days1to6Per = Math.round((days1to6 / totalNonProspecting) * 100);
              this.statsData.nonProspectingDays.days7to15Per = Math.round((days7to15 / totalNonProspecting) * 100);
              this.statsData.nonProspectingDays.days16to30Per = Math.round((days16to30 / totalNonProspecting) * 100);
              this.statsData.nonProspectingDays.days31to45Per = Math.round((days31to45 / totalNonProspecting) * 100);
              this.statsData.nonProspectingDays.days45PlusPer = Math.round((days45Plus / totalNonProspecting) * 100);
            }
            
            // Map sl_warmup_status to Warmup Sync & Warmup Activation panel
            const slWarmupStatus = data.sl_warmup_status || {};
            const warmupTotal = parseInt(slWarmupStatus.total || 0);
            const sync = parseInt(slWarmupStatus.sync || 0);
            const unsync = parseInt(slWarmupStatus.unsync || 0);
            const active = parseInt(slWarmupStatus.active || 0);
            const inactive = parseInt(slWarmupStatus.inactive || 0);
            
            this.statsData.warmupSync.synced = sync;
            this.statsData.warmupSync.notSynced = unsync;
            this.statsData.warmupActivation.active = active;
            this.statsData.warmupActivation.inactive = inactive;
            
            if (warmupTotal > 0) {
              this.statsData.warmupSync.syncedPercent = Math.round((sync / warmupTotal) * 100);
              this.statsData.warmupSync.notSyncedPercent = Math.round((unsync / warmupTotal) * 100);
              this.statsData.warmupActivation.activePercent = Math.round((active / warmupTotal) * 100);
              this.statsData.warmupActivation.inactivePercent = Math.round((inactive / warmupTotal) * 100);
            }
            
            this.statsDataLoaded = true;
          }
        })
        .catch((error) => {
          console.error('Error loading account emails statistics:', error);
          // Keep statsDataLoaded as false to show disabled state
        })
        .finally(() => {
          this.loadingStats = false;
        });
    },
    formatCurrency(value) {
      const num = parseFloat(value || 0);
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.scrollable {
  min-height: 120vh;
}
</style>
